import { type FC } from 'react';
import { useFragment, graphql } from 'react-relay';

import { GridRow } from 'dibs-elements/exports/Grid.tsx';
import { PageLayout } from 'dibs-elements/exports/PageLayout.tsx';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker.tsx';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls.ts';

import { HpSharedModuleContainer } from '../HpSharedModuleContainer/HpSharedModuleContainer.tsx';
import HpSharedShopByModuleItem from './HpSharedShopByModuleItem.tsx';
import { trackShopByItemsImpressions, trackShopByItemClick } from '../helpers/shopByTracking.js';
import { trackModuleLocation, trackModule } from '../helpers/moduleLocationTracking.ts';
import { HpSharedHeader } from '../HpSharedHeader/HpSharedHeader.tsx';

import {
    type HpSharedShopByModule_componentModule$key,
    type HpSharedShopByModule_componentModule$data,
} from './__generated__/HpSharedShopByModule_componentModule.graphql.ts';

const componentModuleFragment = graphql`
    fragment HpSharedShopByModule_componentModule on ShopByModule {
        title
        cmsDisplayTitle
        items {
            linkData {
                path
            }
            ...HpSharedShopByModuleItem_item
        }
    }
`;

export const handleInitialDisplay = ({
    inViewport,
    items,
}: {
    inViewport: boolean;
    items: HpSharedShopByModule_componentModule$data['items'];
}): void => {
    if (items && inViewport) {
        trackShopByItemsImpressions(items);
    }
};

export const HpSharedShopByModule: FC<{
    componentModule: HpSharedShopByModule_componentModule$key;
    useLazyLoadImages: boolean;
    moduleIndex: number;
    totalModules: number;
}> = ({ componentModule: componentModuleRef, useLazyLoadImages, moduleIndex, totalModules }) => {
    const componentModule = useFragment(componentModuleFragment, componentModuleRef);

    if (!componentModule) {
        return null;
    }

    const { items, title, cmsDisplayTitle } = componentModule;

    return (
        <HpSharedModuleContainer addTopGap>
            <PageLayout setViewportBackground="sassyColorWhite">
                <HpSharedHeader title={title} dataTn="shop-by" />
                <VisibilityTracker
                    onVisibilityChange={({ isVisible }) =>
                        handleInitialDisplay({ inViewport: isVisible, items })
                    }
                />
                <GridRow>
                    {(items?.filter(filterNulls) || []).map((item, index) => {
                        return (
                            <HpSharedShopByModuleItem
                                key={`${item?.linkData?.path}-${index}`}
                                item={item}
                                onClick={() => {
                                    trackShopByItemClick(item, index);
                                    trackModuleLocation({
                                        label: 'eight block module',
                                        moduleIndex,
                                        totalModules,
                                    });
                                    trackModule(cmsDisplayTitle);
                                }}
                                useLazyLoadImages={useLazyLoadImages}
                            />
                        );
                    })}
                </GridRow>
            </PageLayout>
        </HpSharedModuleContainer>
    );
};
